import React, { useState, Suspense } from 'react';
import './i18n';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ModalProvider } from './context/ModalContext';
import { ToastProvider } from './context/toastContext';
import { AlertProvider } from './context/AlertContext';
import { Provider } from 'react-redux';
import store from './redux/store';
import useLanguage from './hooks/useLanguage';

import ListingPage from './pages/ListingPage';
import EventDetail from './pages/EventDetail';
import ApplyRedirect from './pages/ApplyRedirect';
import EventListMap from './pages/EventListMap';
import Register from './pages/Register';
import InteractiveMap1 from './pages/InteractiveMap1';
import Dashboard from './pages/Dashboard';
import EventCreation from './pages/EventCreation';
import Homepage from './pages/Homepage';
import BrandDetail from './pages/BrandDetail';
import Payments from './pages/Payments';
import Checkout from './pages/Checkout';
import EmailConfirmation from './pages/EmailConfirmation';
import EmailChangeConfirmation from './pages/EmailChangeConfirmation';
import ResetPassword from './pages/ResetPassword';
import PurposePage from './pages/Purpose';
import ContactUs from './pages/ContactUs';
import TermsConditionsPage from './pages/TermsConditions';
import CookiePolicyPage from './pages/CookiePolicy';

import Layout from './components/Layout';
import LoginModal from './components/LoginModal';
import PrivateRoute from './components/PrivateRoute';
import AuthBootstrapper from './components/AuthBootstrapper';

import ScrollToTop from 'utils/scrollToTop';
import PageviewTracker from './components/PageviewTracker';

function App() {
  useLanguage();

  const GATE_KEY = 'there2b:site_gate_ok';

  const [authenticated, setAuthenticated] = useState(() => {
    return sessionStorage.getItem(GATE_KEY) === '1';
  });

  const [inputPassword, setInputPassword] = useState('');
  const PASSWORD = process.env.REACT_APP_WEBSITE_ACCESS_PASSWORD;

  // ✅ Don’t use NODE_ENV for this (see section 2)
  const gateEnabled = process.env.REACT_APP_SITE_GATE === 'true';

  const handleLogin = () => {
    if (inputPassword === PASSWORD) {
      sessionStorage.setItem(GATE_KEY, '1');
      setAuthenticated(true);
    } else {
      alert('Invalid password! Please try again.');
    }
  };

  if (gateEnabled && !authenticated) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <h1>Restricted Access</h1>
        <input
          type="password"
          placeholder="Enter Password"
          value={inputPassword}
          onChange={(e) => setInputPassword(e.target.value)}
          style={{ marginBottom: '10px', padding: '8px', fontSize: '16px' }}
        />
        <button
          onClick={handleLogin}
          style={{ padding: '8px 16px', fontSize: '16px' }}
        >
          Login
        </button>
      </div>
    );
  }

  return (
    <Suspense fallback={null}>
    <AlertProvider>
      <ToastProvider>
        <ModalProvider>
          <AuthBootstrapper>
            <Router>
              <ScrollToTop />
              <PageviewTracker />
              <Routes>
                <Route
                  path="/"
                  element={
                    <Layout stickyHeader={true}>
                      <Homepage />
                    </Layout>
                  }
                />
                <Route
                  path="/listings"
                  element={
                    <Layout stickyHeader={true}>
                      <ListingPage />
                    </Layout>
                  }
                />
                <Route
                  path="/interactive-map1"
                  element={
                    <Layout>
                      <InteractiveMap1 />
                    </Layout>
                  }
                />
                <Route
                  path="/event/:eventSlug"
                  element={
                    <Layout stickyHeader={true} noOverflow>
                      <EventDetail />
                    </Layout>
                  }
                />
                <Route
                  path="/apply/:eventId"
                  element={
                    <Layout stickyHeader={true}>
                      <ApplyRedirect />
                    </Layout>
                  }
                />
                <Route
                  path="/events/map"
                  element={
                    <Layout stickyHeader={true}>
                      <EventListMap />
                    </Layout>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <Layout stickyHeader={true}>
                      <Register />
                    </Layout>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Layout stickyHeader={true}>
                        <Dashboard />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/create-event"
                  element={
                    <PrivateRoute requireRole="promoter">
                      <Layout stickyHeader={true} noFooter>
                        <EventCreation />
                      </Layout>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/brands/:brandSlug"
                  element={
                    <Layout stickyHeader={true}>
                      <BrandDetail />
                    </Layout>
                  }
                />
                <Route
                  path="/payments"
                  element={
                    <Layout stickyHeader={true}>
                      <Payments />
                    </Layout>
                  }
                />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/confirm-email" element={<EmailConfirmation />} />
                <Route
                  path="/confirm-email-change"
                  element={<EmailChangeConfirmation />}
                />
                <Route path="/purpose" element={<PurposePage />} />
                <Route
                  path="/terms-conditions"
                  element={<TermsConditionsPage />}
                />
                <Route path="/terms" element={<TermsConditionsPage />} />
                <Route path="/privacy" element={<TermsConditionsPage />} />
                <Route path="/cookie-policy" element={<CookiePolicyPage />} />
                <Route path="/cookies" element={<CookiePolicyPage />} />
                <Route
                  path="/contact-us"
                  element={
                    <Layout stickyHeader={true}>
                      <ContactUs />
                    </Layout>
                  }
                />
              </Routes>
              <LoginModal />
            </Router>
          </AuthBootstrapper>
        </ModalProvider>
      </ToastProvider>
    </AlertProvider>
    </Suspense>
  );
}

const RootApp = () => (
  <Provider store={store}>
    <App />
  </Provider>
);

export default RootApp;
